import classNames from 'classnames';
import { CSSProperties, FC } from 'react';
import moment from 'utils/dayjs';

import { formatStartAndEndTimes } from 'utils/time';
import {
  AVAILABILITY_STATUSES,
  AvailabilityStatus,
} from '../../constants/fieldData';

interface Props {
  openingTime?: string;
  startTime: string;
  endTime: string;
  showSlotTimes?: boolean;
  availabilityStatus?: AvailabilityStatus;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  relativeSizeAndPosition?: boolean;
}

const noOp = () => {};

const BookingSlot: FC<Props> = ({
  openingTime,
  startTime,
  endTime,
  showSlotTimes = true,
  availabilityStatus: availabilityStatusProp = AVAILABILITY_STATUSES.AVAILABLE,
  selected = false,
  disabled = false,
  onClick = noOp,
  relativeSizeAndPosition = false,
}) => {
  const readOnly = onClick === noOp;

  const availabilityStatus: AvailabilityStatus = selected
    ? availabilityStatusProp
    : AVAILABILITY_STATUSES.UNAVAILABLE;

  const getLabel = () => {
    if (selected || disabled) return availabilityStatus.label;
    return readOnly ? 'SLOT AVAILABLE' : 'SELECT SLOT';
  };

  const hasIcon =
    availabilityStatus.iconClasses && availabilityStatus.iconClasses.length > 0;

  const { slotClasses = [] } = selected || disabled ? availabilityStatus : {};

  const buttonClasses = classNames('booking-slot__btn', slotClasses, {
    'booking-slot__btn--selected': selected,
    'booking-slot__btn--no-times': !showSlotTimes,
  });

  const iconClasses = classNames(
    'fas',
    'booking-slot__slot-icon',
    availabilityStatus.iconClasses
  );

  const start = moment(startTime, 'HH:mm');
  const offset =
    moment.duration(start.diff(moment(openingTime, 'HH:mm'))).asHours() *
    50 *
    2;

  const timeDifferenceInHours =
    moment
      .duration(moment(endTime, 'HH:mm').diff(moment(startTime, 'HH:mm')))
      .asHours() *
    50 *
    2;
  const allDay = timeDifferenceInHours >= 600;

  const style: CSSProperties = relativeSizeAndPosition
    ? {
        position: 'absolute',
        left: `${offset}px`,
        width: `${timeDifferenceInHours}px`,
        top: allDay ? '5.5rem' : '1rem',
      }
    : {};

  return (
    <li className="booking-slot" style={style}>
      <button
        type="button"
        className={buttonClasses}
        onClick={onClick}
        disabled={disabled}
        data-testid="bookingSlot"
        data-cy="bookingSlot"
      >
        {showSlotTimes && (
          <span>{formatStartAndEndTimes(startTime, endTime, 'HH:mm')}</span>
        )}
        <span data-testid="bookingSlotAvailability" data-cy="availability">
          {getLabel()}
          {hasIcon && <i className={iconClasses} data-testid="icon" />}
        </span>
      </button>
    </li>
  );
};

export default BookingSlot;
